import React, { useState, useEffect } from 'react';

/**
 * Shared Media Lightbox Modal component for GymGhost
 * Displays single animated GIF / image media clean and direct, with loading spinners, error fallbacks, floating close button, and ESC key listener.
 */
export const MediaLightboxModal = ({ mediaData, onClose, lang = 'pl' }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Extract primary animated GIF or image URL
  const activeUrl = mediaData?.gif_url || 
                    mediaData?.url || 
                    mediaData?.imageUrl || 
                    mediaData?.image || 
                    (Array.isArray(mediaData?.images) && mediaData.images[0]) || 
                    "";

  // Reset loading and error when activeUrl changes
  useEffect(() => {
    if (!activeUrl) {
      setIsLoading(false);
      setHasError(true);
    } else {
      setIsLoading(true);
      setHasError(false);
    }
  }, [activeUrl]);

  // Global ESC key listener to close modal safely
  useEffect(() => {
    if (!mediaData) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mediaData, onClose]);

  if (!mediaData) return null;

  return (
    <div 
      className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 transition-all duration-200"
      onClick={onClose}
    >
      {/* Floating Screen-level Close Button */}
      <button
        type="button"
        onClick={onClose}
        className="fixed top-4 right-4 sm:top-6 sm:right-6 z-[10001] w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-slate-900/90 hover:bg-rose-600 text-slate-200 hover:text-white flex items-center justify-center cursor-pointer transition border-2 border-slate-700 hover:border-rose-500 shadow-2xl active:scale-95"
        title={lang === 'pl' ? "Zamknij podgląd (ESC)" : "Close media viewer (ESC)"}
      >
        <span className="material-symbols-outlined text-2xl font-bold">close</span>
      </button>

      <div 
        className="relative max-w-3xl max-h-[92vh] bg-slate-900 border border-slate-700/90 rounded-2xl overflow-hidden shadow-2xl p-4 md:p-5 flex flex-col items-center gap-4 w-full z-[10000]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Bar */}
        <div className="w-full flex items-center justify-between border-b border-slate-800 pb-3 pr-10">
          <div className="flex items-center gap-2.5 min-w-0 pr-2">
            <span className="w-8 h-8 rounded-lg bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-400 shrink-0">
              <span className="material-symbols-outlined text-base">gif</span>
            </span>
            <h3 className="text-sm md:text-base font-black text-slate-100 truncate m-0 tracking-wide">
              {mediaData.title || (lang === 'pl' ? 'Podgląd Ćwiczenia' : 'Exercise Media')}
            </h3>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white flex items-center justify-center cursor-pointer transition border border-slate-700 shrink-0 active:scale-95 shadow"
            title={lang === 'pl' ? "Zamknij (ESC)" : "Close (ESC)"}
          >
            <span className="material-symbols-outlined text-lg">close</span>
          </button>
        </div>

        {/* Image / GIF Container with Loading Spinner & Error Fallback */}
        <div className="relative w-full max-h-[70vh] flex items-center justify-center overflow-hidden rounded-xl bg-slate-950 border border-slate-800 p-2 min-h-[260px]">
          {/* Loading Spinner */}
          {isLoading && !hasError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5 text-indigo-400 bg-slate-950/90 z-10">
              <span className="material-symbols-outlined text-4xl animate-spin">progress_activity</span>
              <span className="text-xs font-extrabold text-slate-300 tracking-wide">
                {lang === 'pl' ? 'Wczytywanie animowanego GIFa ćwiczenia...' : 'Loading animated exercise GIF...'}
              </span>
            </div>
          )}

          {/* Error Fallback */}
          {hasError ? (
            <div className="flex flex-col items-center justify-center gap-3 text-rose-400 py-10 px-4 text-center max-w-md">
              <span className="material-symbols-outlined text-5xl text-rose-500">broken_image</span>
              <p className="text-xs md:text-sm font-bold text-slate-300 m-0">
                {lang === 'pl'
                  ? 'Brak poprawnego podglądu pliku graficznego lub nie udało się go pobrać z serwera.'
                  : 'Unable to render direct image media inside the browser frame.'}
              </p>
              {activeUrl && (
                <a 
                  href={activeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="mt-2 inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-black px-4 py-2.5 rounded-xl transition cursor-pointer shadow-lg active:scale-95"
                >
                  <span>{lang === 'pl' ? 'Otwórz grafikę w nowej karcie' : 'Open media in new tab'}</span>
                  <span className="material-symbols-outlined text-sm">open_in_new</span>
                </a>
              )}
            </div>
          ) : (
            <img 
              src={activeUrl} 
              alt={mediaData.title || "Exercise media"} 
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
              className={`max-w-full max-h-[66vh] object-contain rounded-lg shadow-xl transition-all duration-300 ${
                isLoading ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
              }`} 
            />
          )}
        </div>

        {/* Footer Hint */}
        <div className="w-full flex items-center justify-between text-[11px] font-semibold text-slate-400 px-1">
          <span>{lang === 'pl' ? 'Naciśnij ESC, przycisk X lub kliknij poza oknem, aby zamknąć' : 'Press ESC, X button, or click outside to dismiss'}</span>
        </div>
      </div>
    </div>
  );
};

export default MediaLightboxModal;
